import axios from 'axios'
import { handleCatchError, handleActionDispatch } from 'shared/utilities/actionUtils'
import { handleCompletedRequest } from 'Store/Loading/actions'
import FileDownload from 'js-file-download'

export const Axios = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 120000, // 2 min
  headers: { 'Content-type': 'application/json' },
})

// HPD and other city data sources were imported with Windows-1252 encoding.
// The Unicode replacement character (U+FFFD) was substituted for unrecognised
// bytes and then double-encoded, producing the visible artefact "ï¿½".
// Replace it with an em dash, which is the character most commonly lost.
const sanitizeEncodingArtefacts = value => {
  if (typeof value === 'string') return value.replace(/ï¿½/g, '—')
  if (Array.isArray(value)) return value.map(sanitizeEncodingArtefacts)
  if (value !== null && typeof value === 'object') {
    const out = {}
    for (const key of Object.keys(value)) out[key] = sanitizeEncodingArtefacts(value[key])
    return out
  }
  return value
}

// Transient server/network errors (the intermittent "Oops, something went
// wrong" that a refresh fixes) auto-retry on idempotent GETs only. POST/PATCH/
// DELETE are never retried, to avoid duplicate side effects (e.g. bookmarking
// twice). Client timeouts are skipped — retrying a huge timed-out query just
// times out again.
const RETRYABLE_STATUSES = [500, 502, 503, 504]
const MAX_RETRIES = 2

Axios.interceptors.response.use(
  response => {
    response.data = sanitizeEncodingArtefacts(response.data)
    return response
  },
  async error => {
    const config = error.config || {}
    const method = (config.method || '').toLowerCase()
    const status = error.response && error.response.status
    const isTimeout =
      error.code === 'ECONNABORTED' || (error.message && /timeout/i.test(error.message))
    const isTransient = !isTimeout && (RETRYABLE_STATUSES.includes(status) || !error.response)

    config.__retryCount = config.__retryCount || 0
    if (method === 'get' && isTransient && config.__retryCount < MAX_RETRIES) {
      config.__retryCount += 1
      await new Promise(resolve => setTimeout(resolve, 500 * config.__retryCount))
      return Axios(config)
    }
    return Promise.reject(error)
  }
)



export const constructAxiosGet = (
  dispatch,
  getState,
  requestId,
  url,
  params,
  access_token,
  constant,
  dispatchAction,
  handleAction,
  requestOptions = {}
) => {
  if (!requestId) throw 'Misconfigured Axios Request (missing id)'
  handleActionDispatch(dispatch, constant, requestId)
  const isExternal = url.indexOf('https') !== -1
  let opts = {
    params: { format: 'json', ...params },
    headers: typeof access_token === 'string' ? { authorization: `Bearer ${access_token}` } : null,
    ...requestOptions,
  }
  if (isExternal) {
    opts = { ...requestOptions }
  }

  return Axios.get(url, opts)
    .then(response => {
      const requestIsValid = !!getState().loading.requests.filter(request => request.id === requestId).length
      if (requestIsValid) {
        if (dispatchAction) {
          dispatch(dispatchAction(response, constant, dispatch))
        }

        if (handleAction) {
          handleAction()
        }
        dispatch(handleCompletedRequest(constant, requestId))

        if (constant.includes('_CSV')) {
          FileDownload(response.data, response.config.params.filename)
        }
      }
    })
    .catch(error => {
      handleCatchError(error, constant, dispatch, requestId)
    })
}

export const constructAxiosPost = (
  dispatch,
  getState,
  requestId,
  url,
  body,
  params,
  access_token,
  constant,
  dispatchAction,
  handleAction
) => {
  if (!requestId) throw 'Misconfigured Axios Request (missing id)'
  handleActionDispatch(dispatch, constant, requestId)
  const headers = {
    'Content-Type': 'application/json',
  };

  if (typeof access_token === 'string') {
    headers['authorization'] = `Bearer ${access_token}`;
  }

  return Axios.post(url, body, {
    params: { format: 'json', ...params },
    headers: headers,
  })
    .then(response => {
      const requestIsValid = !!getState().loading.requests.filter(request => request.id === requestId).length
      if (requestIsValid) {
        if (dispatchAction) {
          dispatch(dispatchAction(response, constant))
        }

        if (handleAction) {
          handleAction()
        }
        dispatch(handleCompletedRequest(constant, requestId))

        if (response.headers && response.headers['content-type'] && response.headers['content-type'].match(/csv/)) {
          FileDownload(response.data, response.config.params.filename)
        }
      }
    })
    .catch(error => {
      handleCatchError(error, constant, dispatch, requestId)
    })
}

export const constructAxiosDelete = (
  dispatch,
  getState,
  requestId,
  url,
  body,
  params,
  access_token,
  constant,
  dispatchAction,
  handleAction
) => {
  if (!requestId) throw 'Misconfigured Axios Request (missing id)'
  handleActionDispatch(dispatch, constant, requestId)
  return Axios.delete(url,
    {
      params: { format: 'json', ...params },
      data: body,
      headers: typeof access_token === 'string' ? { authorization: `Bearer ${access_token}` } : null,
    })
    .then(response => {
      const requestIsValid = !!getState().loading.requests.filter(request => request.id === requestId).length
      if (requestIsValid) {
        if (dispatchAction) {
          dispatch(dispatchAction(response, constant))
        }

        if (handleAction) {
          handleAction()
        }
        dispatch(handleCompletedRequest(constant, requestId))
      }
    })
    .catch(error => {
      handleCatchError(error, constant, dispatch, requestId)
    })
}

export const constructAxiosPatch = (
  dispatch,
  getState,
  requestId,
  url,
  body,
  params,
  access_token,
  constant,
  dispatchAction,
  handleAction
) => {
  if (!requestId) throw 'Misconfigured Axios Request (missing id)'
  handleActionDispatch(dispatch, constant, requestId)
  return Axios.patch(url, body, {
    params: { format: 'json', ...params },
    headers: typeof access_token === 'string' ? { authorization: `Bearer ${access_token}` } : null,
  })
    .then(response => {
      const requestIsValid = !!getState().loading.requests.filter(request => request.id === requestId).length
      if (requestIsValid) {
        if (dispatchAction) {
          dispatch(dispatchAction(response, constant))
        }

        if (handleAction) {
          handleAction()
        }
        dispatch(handleCompletedRequest(constant, requestId))

        if (response.headers && response.headers['content-type'].match(/csv/)) {
          FileDownload(response.data, response.config.params.filename)
        }
      }
    })
    .catch(error => {
      handleCatchError(error, constant, dispatch, requestId)
    })
}